import { SortingOrder, TradeData } from "@shared/types";
import { useMemo, useState } from "react";
import { getSortedDataBySortSelect } from "@shared/utils/general";

export const useTradesSort = (
  data: TradeData[],
  isOpenTrades: boolean,
  defaultOpenTradesSort = TradesTableSort.NEXT_TAKE_PROFIT,
  defaultOpenTradesSortOrder = SortingOrder.ASCEND
) => {
  const [sortByWithType, setSortByWithType] = useState<
    TradesTypeDictionary<TradesTableSort>
  >({
    [GeneralTradeStatus.OPEN]: defaultOpenTradesSort,
    [GeneralTradeStatus.CLOSED]: TradesTableSort.CLOSE_TIME,
  });

  const [sortDirectionWithType, setSortDirectionWithType] = useState<
    TradesTypeDictionary<SortingOrder>
  >({
    [GeneralTradeStatus.OPEN]: defaultOpenTradesSortOrder,
    [GeneralTradeStatus.CLOSED]: SortingOrder.DESCEND,
  });

  const tradesType = isOpenTrades
    ? GeneralTradeStatus.OPEN
    : GeneralTradeStatus.CLOSED;

  const sortedData = useMemo(() => {
    const currentSortBy = sortByWithType[tradesType];
    const currentSortDirection = sortDirectionWithType[tradesType];
    return getSortedDataBySortSelect<TradeData, TradesTableSort>(
      data,
      currentSortBy,
      currentSortDirection,
      SORT_TO_PATH,
      STRING_SORTS,
      ABS_SORTS
    );
  }, [data, sortByWithType, sortDirectionWithType]);

  return {
    sortedData,
    sortBy: sortByWithType[tradesType],
    setSortBy: (sortBy: TradesTableSort) =>
      setSortByWithType({
        ...sortByWithType,
        [tradesType]: sortBy,
      }),
    sortDirection: sortDirectionWithType[tradesType],
    setSortDirection: (sortOrder: SortingOrder) =>
      setSortDirectionWithType({
        ...sortDirectionWithType,
        [tradesType]: sortOrder,
      }),
  };
};

export enum TradesTableSort {
  SYMBOL,
  AMOUNT,
  LEVERAGE,
  NEXT_ENTRY_OR_STOP,
  NEXT_TAKE_PROFIT,
  OPEN_TIME,
  CLOSE_TIME,
  CHANGE,
  PROGRESS,
}

export const FIELD_NAME_TO_SORT = {
  symbol: TradesTableSort.SYMBOL,
  amount: TradesTableSort.AMOUNT,
  leverage: TradesTableSort.LEVERAGE,
  next_entry_or_stop: TradesTableSort.NEXT_ENTRY_OR_STOP,
  next_take_profit: TradesTableSort.NEXT_TAKE_PROFIT,
  open_time: TradesTableSort.OPEN_TIME,
  close_time: TradesTableSort.CLOSE_TIME,
  change: TradesTableSort.CHANGE,
  progress: TradesTableSort.PROGRESS,
};

export const SORT_TO_PATH: {
  [sortKey in TradesTableSort]?: string | string[];
} = {
  [TradesTableSort.SYMBOL]: "symbol",
  [TradesTableSort.AMOUNT]: "allocated_amount_usd",
  [TradesTableSort.LEVERAGE]: "leverage",
  [TradesTableSort.NEXT_ENTRY_OR_STOP]: [
    "next_entry.distance",
    "next_stop.distance",
  ],
  [TradesTableSort.NEXT_TAKE_PROFIT]: "next_take_profit.distance",
  [TradesTableSort.OPEN_TIME]: "timestamp",
  [TradesTableSort.CLOSE_TIME]: "timestamp",
  [TradesTableSort.CHANGE]: "usd_amount_change",
  [TradesTableSort.PROGRESS]: "graph_data.profit",
};

export const ABS_SORTS = [
  TradesTableSort.NEXT_TAKE_PROFIT,
  TradesTableSort.NEXT_ENTRY_OR_STOP,
];

export const STRING_SORTS = [TradesTableSort.SYMBOL];

export enum GeneralTradeStatus {
  OPEN = "open",
  CLOSED = "closed",
}

type TradesTypeDictionary<T> = Record<GeneralTradeStatus, T>;
